import React from "react";
import { Link } from "react-router-dom";
import { MRT_ColumnDef } from "material-react-table";
import { Box, Tooltip } from "@mui/material";
import dayjs from "dayjs";

import { AuditLogIcon, EsgFileIcon } from "assets";
import { getInitials, isNullOrUndefined } from "utils";
import { Avatar, EsgPrimaryLabel } from "components";
import { EMPTY_CELL_VALUE } from "consts";

export const AUDIT_LOGS_PAGE = {
  ABSOLUTE_PATH: "/audit-page",
  TITLE: "Audit Logs",
  ICON: <AuditLogIcon />,
  IS_TBD: false,
  renderMenu: false,
};

export const AUDIT_LOGS_COLUMNS_MAIN: MRT_ColumnDef<any>[] = [
  {
    header: "Activity Type",
    accessorKey: "eventType",
    Cell: ({ cell }) => {
      const value = cell.getValue()
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      return (
        <Box sx={{ display: "flex", width: '70px' }}>
          <Tooltip title={value as string}>
            <Box>
              {value as any}
            </Box>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    header: "Resources Affected",
    accessorKey: "className",
    Cell: ({ cell, row }) => {
      const value = cell.getValue()

      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      return (
        <Box sx={{ display: "flex", width: '30px' }}>
          <Tooltip title={value as string}>
            {row.original?.className === "Bill" ?
              <Link style={{ textDecoration: 'none', color: 'black' }} target="_blank" to={`/documents/summary/${row.original?.persistedObjectId}`}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <EsgFileIcon />
                  <EsgPrimaryLabel sx={{ mt: '4px' }}>{value as any}</EsgPrimaryLabel>
                </Box>
              </Link> :
              <Box>
                {value as any}
              </Box>}
          </Tooltip>
        </Box>
      );
    },
  },
  {
    header: "User",
    accessorKey: "userName",
    filterFn: (row: any, _id: any, filterValue: any) => {
      const search = filterValue.toLowerCase();
      const userName = (row.original?.userName ?? "").toLowerCase();
      const firstName = (row.original?.firstName ?? "").toLowerCase();
      const lastName = (row.original?.lastName ?? "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      return userName.includes(search) || fullName.includes(search);
    },
    Cell: ({ cell, row }) => {
      const value = cell.getValue()
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }

      const firstName = row.original?.firstName
      const lastName = row.original?.lastName

      const uploaderName = `${firstName} ${lastName}`;

      return (
        <Box sx={{ display: "flex" }}>
          <Tooltip title={value as string}>
            <Box sx={{ width: "50px" }}>
              <Avatar
                sx={{
                  width: "34px",
                  height: "34px",
                }}
              >
                {getInitials(uploaderName)}
              </Avatar>
            </Box>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    header: "TimeStamp",
    accessorKey: "createDate",
    filterFn: (row: any, id: any, filterValue: any) => {
      const search = filterValue.toLowerCase();
      const date = dayjs(row.original?.createDate).format('YYYY/MM/DD HH:mm:ss');
      return date.toString().toLowerCase().includes(search);
    },
    Cell: ({ cell }) => {
      const value = cell.getValue()
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }
      return (
        <Box sx={{ display: "flex", width: '30px' }}>
          <Tooltip title={value as string}>
            <Box>
              {dayjs(value as string).format('YYYY/MM/DD HH:mm:ss') as any}
            </Box>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    header: "Change Log",
    accessorKey: "context",
    filterFn: (row: any, id: any, filterValue: any) => {
      const search = filterValue.toLowerCase();
      const context = JSON.parse(row.original?.context);
      const keysToShow = Object.keys(context).slice(0, 2);
      const valuesToShow = keysToShow.map((key) => context[key]).map((item) => Object.values(item)).flat();

      const allValues = [...keysToShow, ...valuesToShow].map((item: any) => item && item.toString().toLowerCase()) ?? [];

      return allValues.some((value: any) => value?.toString().toLowerCase().includes(search));
    },
    Cell: ({ cell }) => {
      const value = cell.getValue()
      const context = JSON.parse(value as string)

      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }

      const keysToShow = Object.keys(context).slice(0, 2);

      const tooltipData = <ul>
        {Object.keys(context).map((key) => (
          <li key={key}>
            {key} : {context[key].oldValue} {"->"} {context[key].newValue}
          </li>
        ))}
      </ul>

      return (
        <Box sx={{ display: "flex", width: "150px" }}>
          <Tooltip title={tooltipData as any}>
            <div>
              {keysToShow.map((key) => {
                if (key === "parentData") return null
                return (
                  <div key={key}>
                    • {key} : {context[key].oldValue} {"->"} {context[key].newValue}
                  </div>
                )
              })}
              &nbsp; {Object.keys(context).length > 2 && "... more"}
            </div>
          </Tooltip>
        </Box>
      );
    },
  },
];